import { boilerplatesStore } from '../stores/boilerplatesStore';

// Regex pattern for matching @{figure:...} and @{numeral:...} references
const REFERENCE_PATTERN = /@\{(figure|numeral):([^}]+)\}/g;

/**
 * Parse text for inline figure/numeral references and create text nodes with marks
 * Matches the same pattern used in specRegenerationService for limitations
 * @param {string} text - Text to parse
 * @param {Object} schema - ProseMirror schema
 * @returns {Array} Array of text nodes with appropriate marks
 */
function parseInlineReferences(text, schema) {
    const textNodes = [];
    let lastIndex = 0;
    let match;

    // Reset regex lastIndex
    REFERENCE_PATTERN.lastIndex = 0;

    while ((match = REFERENCE_PATTERN.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            const beforeText = text.slice(lastIndex, match.index);
            if (beforeText) {
                textNodes.push(schema.text(beforeText));
            }
        }

        // Figure or numeral reference (@{figure:...} or @{numeral:...})
        const fullMatch = match[0];     // e.g., "@{figure:ar_device}"
        const refType = match[1];       // "figure" or "numeral"
        const refContent = match[2];    // e.g., "ar_device" or "head_102"

        // Create a placeholder figureKey - works even if figure doesn't exist
        const figureKey = `generated:${refType}:${refContent}`;

        const figureRefMark = schema.marks.figureReference;
        if (figureRefMark) {
            textNodes.push(schema.text(fullMatch, [figureRefMark.create({ figureKey })]));
        } else {
            // Fallback if figureReference mark doesn't exist
            textNodes.push(schema.text(fullMatch));
        }

        lastIndex = REFERENCE_PATTERN.lastIndex;
    }

    // Add remaining text after last match
    if (lastIndex < text.length) {
        const afterText = text.slice(lastIndex);
        if (afterText) {
            textNodes.push(schema.text(afterText));
        }
    }

    // If no matches found, return the original text
    if (textNodes.length === 0 && text) {
        textNodes.push(schema.text(text));
    }

    return textNodes;
}

export class BoilerplateService {
    constructor() {
        this.unsubscribe = null;
        this.boilerplates = [];

        // Subscribe to boilerplatesStore
        this.unsubscribe = boilerplatesStore.subscribe(plates => {
            this.boilerplates = plates;
        });
    }

    insertBoilerplate(editor, boilerplateName, content = null) {
        if (!editor || typeof editor.chain !== 'function') {
            console.warn('Editor must have a chain method');
            return false;
        }

        // Get boilerplate content
        let boilerplateContent = content;
        if (boilerplateContent === null || boilerplateContent === undefined) {
            const boilerplate = this.boilerplates.find(b => b.name === boilerplateName);
            if (!boilerplate) {
                throw new Error(`Boilerplate "${boilerplateName}" not found`);
            }
            boilerplateContent = boilerplate.content;
        }

        const trimmedContent = boilerplateContent.trim();

        // Handle empty content
        if (!trimmedContent) {
            return editor
                .chain()
                .focus()
                .insertContent({
                    type: 'paragraph',
                    attrs: {
                        class: 'boilerplate-block',
                        blockType: 'boilerplate',
                        name: boilerplateName
                    }
                })
                .run();
        }

        // Split content into paragraph texts
        const paragraphTexts = trimmedContent
            .split(/\n/)
            .filter(p => p.trim())
            .map(text => text.trim());

        // Use a single transaction to insert all content with parsed references
        return editor
            .chain()
            .focus()
            .command(({ tr, state }) => {
                const { $from } = tr.selection;
                const currentBlock = $from.parent;
                const blockStart = $from.before($from.depth);
                const blockEnd = $from.after($from.depth);

                const paragraphAttrs = {
                    class: 'boilerplate-block',
                    blockType: 'boilerplate',
                    name: boilerplateName
                };

                // Create paragraph nodes with parsed inline references
                const paragraphNodes = paragraphTexts.map(text => {
                    const textNodes = parseInlineReferences(text, state.schema);
                    return state.schema.nodes.paragraph.create(paragraphAttrs, textNodes);
                });

                // If we're in an empty paragraph, replace it entirely
                if (currentBlock.type.name === 'paragraph' && currentBlock.content.size === 0) {
                    tr.replaceWith(blockStart, blockEnd, paragraphNodes);
                } else {
                    // Otherwise, insert content at current position
                    tr.insert(tr.selection.from, paragraphNodes);
                }

                return true;
            })
            .run();
    }

    getBoilerplates() {
        return this.boilerplates;
    }

    getBoilerplateMenuItems() {
        // Convert boilerplates to menu items format
        return this.boilerplates.map(boilerplate => ({
            id: boilerplate.id,
            title: boilerplate.name,
            command: 'setBoilerplate',
            attrs: {
                name: boilerplate.name,
                content: boilerplate.content,
                blockType: 'boilerplate'
            }
        }));
    }

    destroy() {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
    }
}

// Create a singleton instance
export const boilerplateService = new BoilerplateService();